/**
 * Page loading UX.
 *
 * Replaces the full-size spinner circle with two quieter signals:
 *   1. A thin progress bar pinned to the top of the viewport.
 *   2. Shimmer skeletons on media that hasn't decoded yet.
 *
 * Design rules that matter here:
 *   - Nothing appears for the first SHOW_DELAY ms. Most navigations finish
 *     inside that window, and a loader that flashes for 80ms reads as jank.
 *   - The bar eases toward 90% and waits there. It never claims to know how
 *     far along a navigation is, because it can't.
 *   - Back/forward restores from bfcache without running load handlers, so
 *     `pageshow` has to clear the bar explicitly or it sticks forever.
 */
(function () {
  'use strict';

  const SHOW_DELAY = 250; // ms before the bar is allowed to appear
  const TRICKLE_INTERVAL = 300;
  const CEILING = 0.9; // never imply completion we haven't observed
  const MAX_LIFETIME = 20000; // give up rather than leave a bar stuck at 90%

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let el = null;
  let bar = null;
  let showTimer = null;
  let trickleTimer = null;
  let bailTimer = null;
  let progress = 0;
  let visible = false;

  function mount() {
    if (el) return;
    el = document.createElement('div');
    el.className = 's01-progress';
    // Purely decorative. An indeterminate bar tells a screen reader nothing
    // it doesn't already get from the browser's own navigation announcement,
    // and role="progressbar" without a value is just noise.
    el.setAttribute('aria-hidden', 'true');
    bar = document.createElement('div');
    bar.className = 's01-progress__bar';
    el.appendChild(bar);
    document.body.appendChild(el);
  }

  function paint() {
    if (bar) bar.style.width = progress * 100 + '%';
  }

  function reveal() {
    mount();
    visible = true;
    el.setAttribute('data-active', '');
    progress = 0.08;
    paint();

    trickleTimer = setInterval(function () {
      // Decelerating approach to the ceiling: big early jumps, then crawl.
      const remaining = CEILING - progress;
      if (remaining <= 0.001) return;
      progress += remaining * 0.18;
      paint();
    }, TRICKLE_INTERVAL);

    // A click we mistook for a navigation (something intercepted it, or the
    // request simply died) would otherwise pin the bar at 90% for the rest of
    // the session. Better to quietly give up.
    bailTimer = setTimeout(done, MAX_LIFETIME);
  }

  function start() {
    if (showTimer || visible) return;
    showTimer = setTimeout(function () {
      showTimer = null;
      reveal();
    }, SHOW_DELAY);
  }

  function done() {
    if (showTimer) {
      clearTimeout(showTimer);
      showTimer = null;
    }
    if (trickleTimer) {
      clearInterval(trickleTimer);
      trickleTimer = null;
    }
    if (bailTimer) {
      clearTimeout(bailTimer);
      bailTimer = null;
    }
    if (!visible) return;

    progress = 1;
    paint();

    const finish = function () {
      if (!el) return;
      el.removeAttribute('data-active');
      visible = false;
      // Reset width only once the fade-out has finished, so the bar doesn't
      // visibly snap back to zero while it's still on screen.
      setTimeout(function () {
        progress = 0;
        paint();
      }, 250);
    };

    reduceMotion ? finish() : setTimeout(finish, 150);
  }

  /* ---------- Navigation detection ---------- */

  function isPlainLeftClick(event) {
    return (
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey &&
      !event.defaultPrevented
    );
  }

  function navigatesAway(anchor) {
    if (!anchor || !anchor.href) return false;
    if (anchor.target && anchor.target !== '_self') return false;
    if (anchor.hasAttribute('download')) return false;

    const url = new URL(anchor.href, window.location.href);
    if (url.origin !== window.location.origin) return false;
    if (!/^https?:$/.test(url.protocol)) return false;

    // Same document, different hash — no load happens.
    const here = window.location.pathname + window.location.search;
    if (url.hash && url.pathname + url.search === here) return false;

    return true;
  }

  // Both listeners run on the bubble phase on purpose. Theme components call
  // preventDefault()/stopPropagation() from their own bubble-phase handlers,
  // so capturing here would fire before we can tell an intercepted click from
  // a real navigation — and would light the bar for fetches that never leave
  // the page.
  document.addEventListener('click', function (event) {
    if (!isPlainLeftClick(event)) return;
    const anchor = event.target.closest && event.target.closest('a[href]');
    if (!navigatesAway(anchor)) return;
    if (anchor.closest('[data-no-progress]')) return;
    start();
  });

  document.addEventListener('submit', function (event) {
    const form = event.target;
    if (!form || event.defaultPrevented) return;
    if (form.closest('[data-no-progress], predictive-search, facet-filters-form')) return;
    start();
  });

  // A navigation the browser started on its own (back button, redirect).
  window.addEventListener('pagehide', done);

  // Restored from bfcache — no load event will fire, so clear state by hand.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) done();
  });

  /* ---------- Initial document load ---------- */

  if (document.readyState !== 'complete') {
    start();
    window.addEventListener('load', done, { once: true });
  }

  /* ---------- In-page fetches (facet filtering, sorting, pagination) ---------- */

  // facets.js doesn't navigate: it flags .collection with `loading`, fetches,
  // then replaces the whole #ProductGridContainer subtree — which throws away
  // the flagged node, so there is no class *removal* to listen for. Watch both
  // the attribute going on and the subtree being swapped out.
  const gridContainer = document.getElementById('ProductGridContainer');
  if (gridContainer) {
    new MutationObserver(function (records) {
      let swapped = false;
      records.forEach(function (record) {
        if (record.type === 'childList' && record.addedNodes.length) {
          swapped = true;
        } else if (
          record.type === 'attributes' &&
          record.target.classList &&
          record.target.classList.contains('collection') &&
          record.target.classList.contains('loading')
        ) {
          start();
        }
      });
      if (swapped) {
        done();
        scan(gridContainer);
      }
    }).observe(gridContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  /* ---------- Media skeletons ---------- */

  // Must stay in sync with the matching selector list in custom.css.
  const SKELETONS = '.s01-skel, .card__media > .media, .product__media';

  function settle(holder) {
    holder.classList.add('s01-skel--done');
  }

  function watch(holder) {
    if (holder.dataset.s01Skel) return;
    holder.dataset.s01Skel = '1';

    const img = holder.querySelector('img');

    // Placeholder SVGs, videos and model-viewers have nothing to wait on.
    if (!img) {
      settle(holder);
      return;
    }

    // Cached images report complete synchronously and fire no load event.
    if (img.complete && img.naturalWidth > 0) {
      settle(holder);
      return;
    }

    const finish = function () {
      settle(holder);
    };
    img.addEventListener('load', finish, { once: true });
    // A broken image must not shimmer forever.
    img.addEventListener('error', finish, { once: true });
  }

  function scan(root) {
    (root || document).querySelectorAll(SKELETONS).forEach(watch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan();
    });
  } else {
    scan();
  }

  // Sections re-render on variant change, quick-add, and in the theme editor.
  new MutationObserver(function (records) {
    records.forEach(function (record) {
      record.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches(SKELETONS)) watch(node);
        if (node.querySelectorAll) scan(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.S01Progress = { start: start, done: done };
})();
